import { Suspense, lazy, useEffect, useRef } from "react";
import Header from "@/components/site/Header";
import Seo from "@/components/site/Seo";
import Hero from "@/components/site/Hero";
import Problem from "@/components/site/Problem";
import Footer from "@/components/site/Footer";
import LazyMount from "@/components/site/LazyMount";

// A real touch-scroll simulation (Puppeteer + CDP touch events, not
// wheel()/scrollBy()) on both the live site and a local build reproduced this
// repeatedly: during a fast scroll, one or more of the LazyMount placeholder
// divs below (each still empty, not yet mounted) briefly gets an injected
// `display: none !important` and reports 0 height, before returning to
// normal a moment later. This only happens with the real React app running
// (a static HTML page with the exact same tall empty divs, scrolled the same
// way, never showed it), so it's tied to React's own rendering/scheduling
// under main-thread load during a fast scroll, not a general browser
// behavior. Whatever the precise trigger, the effect is what matters: it
// drops the page's total scrollable height for a moment, and the browser
// immediately clamps window.scrollY to fit the new (shorter) height —
// landing back up near Hero/CallSimulation/Problem, the only content that
// isn't behind one of these placeholders, regardless of how far the visitor
// had actually scrolled. That's the exact bug reported: an ordinary scroll,
// no link clicked, landing back on "Live Call Simulation".
//
// This neutralizes it directly rather than chasing the exact cause further:
// <main> itself was never observed going display:none in any repro, only its
// children — so tracking <main>'s natural height and never letting it drop
// *immediately* means the overall scrollable area can't suddenly shrink
// while a child's placeholder is mid-collapse, however that happens.
//
// A pure ratchet that NEVER comes back down (the first version of this fix)
// created a second, permanent bug: several sections' LazyMount placeholders
// (minHeightClass) only have two fixed pixel values, one per Tailwind
// breakpoint, but a section's REAL mounted height can scale smoothly across
// a width range in between (e.g. FinalCTA's heading grows through
// text-4xl/md:text-6xl/lg:text-7xl with the viewport, not in one jump at
// md's 768px) — so at many ordinary tablet/laptop widths the placeholder is
// measurably taller than the section's real content ever is. A ratchet that
// can only grow locks in that inflated pre-mount total forever, leaving a
// permanent blank gap between the last section and the footer even after
// every section has mounted to its real, shorter height — confirmed by
// sweeping real widths against the live build: FinalCTA's placeholder
// overshoots its own real height by up to ~193px between roughly 768px and
// 1536px wide.
//
// So: still jump UP the instant anything grows (handles real content
// becoming taller, and rides straight through a transient collapse since
// the very next recovered measurement is >= the current floor). But a drop
// now schedules a "settle" instead of being ignored forever — if the page
// stays at or below that lower total for SETTLE_DELAY_MS with nothing else
// changing (i.e. the drop wasn't a mid-scroll blip, it's the real settled
// layout), commit down to it. Measuring each direct child of <main>
// individually (rather than <main> itself) is what makes this possible at
// all: once we've set <main>'s own min-height, <main>'s OWN box stops
// reflecting its content's true natural height (it's clamped to whatever we
// set), so there'd be nothing left to settle down to. Children never get a
// min-height from us, so their reported heights stay the real, unclamped
// signal throughout.
const SETTLE_DELAY_MS = 500;

function useSettledHeightRatchet(ref) {
  const committedHeight = useRef(0);
  const childHeights = useRef(new Map());
  const settleTimer = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const sumChildHeights = () => {
      let total = 0;
      for (const h of childHeights.current.values()) total += h;
      return total;
    };

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        childHeights.current.set(entry.target, h);
      }
      const sum = sumChildHeights();

      if (sum >= committedHeight.current) {
        committedHeight.current = sum;
        el.style.minHeight = `${sum}px`;
        if (settleTimer.current) {
          clearTimeout(settleTimer.current);
          settleTimer.current = null;
        }
        return;
      }

      if (settleTimer.current) clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => {
        settleTimer.current = null;
        const settled = sumChildHeights();
        committedHeight.current = settled;
        el.style.minHeight = `${settled}px`;
      }, SETTLE_DELAY_MS);
    });

    Array.from(el.children).forEach((child) => ro.observe(child, { box: "border-box" }));

    return () => {
      ro.disconnect();
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [ref]);
}

// The home page was the slowest page to first-load because every section
// below the fold was bundled eagerly into the same chunk as the hero. Every
// other route already loads its own smaller chunk lazily (see App.js) — the
// home page just wasn't split internally the same way. Keep only what's
// needed for the very first screen (Header, Hero, Problem) in the main
// bundle; everything a visitor only reaches by scrolling loads in parallel
// right behind it instead of blocking first paint.
//
// IMPORTANT: React.lazy() below only splits the JS into separate chunks —
// it does NOT wait for scroll. Wrapping each one in <LazyMount> is what
// actually defers both the chunk request AND the mount (and therefore any
// effects/media fetches inside) until the section is scrolled near. Without
// LazyMount, all ten of these were resolving and mounting in parallel the
// instant the page loaded, which is what kept eating mobile load time even
// after individually-oversized assets inside them got fixed.
const CallSimulation = lazy(() => import("@/components/site/CallSimulation"));
const HowItWorks = lazy(() => import("@/components/site/HowItWorks"));
const VoiceLibrary = lazy(() => import("@/components/site/VoiceLibrary"));
const RealWorld = lazy(() => import("@/components/site/RealWorld"));
const CaseStudy = lazy(() => import("@/components/site/CaseStudy"));
const Guardrails = lazy(() => import("@/components/site/Guardrails"));
const Pricing = lazy(() => import("@/components/site/Pricing"));
const RiskReversal = lazy(() => import("@/components/site/RiskReversal"));
const FAQ = lazy(() => import("@/components/site/FAQ"));
const FinalCTA = lazy(() => import("@/components/site/FinalCTA"));

export default function Home() {
  const mainRef = useRef(null);
  useSettledHeightRatchet(mainRef);
  return (
    <div className="relative" data-testid="home-page">
      <Seo
        title="AI Dispatcher for Residential HVAC Companies | Favor Studio"
        description="We help residential HVAC companies stop losing revenue from missed and after-hours calls with an AI front desk that answers, qualifies, and books customers 24/7."
        path="/"
      />
      <Header />
      <main ref={mainRef}>
        <Hero />
        {/* Each LazyMount+lazy() pair gets its OWN Suspense boundary. A single
            shared boundary around all of them (the previous structure) meant
            that whenever any ONE section's chunk started loading -- its
            IntersectionObserver firing as a visitor scrolled past it -- React
            replaced the fallback (null) for the ENTIRE boundary, instantly
            collapsing every sibling already on screen (including Problem,
            which isn't even lazy) to zero height. That collapsed the whole
            page's scroll height for a moment, and the browser clamped
            window.scrollY down to fit the new (much shorter) height, landing
            back inside the Hero/CallSimulation area regardless of how far the
            visitor had actually scrolled -- reproduced with a real touch-
            scroll simulation (Puppeteer, CDP touch events, not wheel()) that
            recorded every sibling's height dropping to 0px in lockstep the
            instant a not-yet-loaded section entered view, and window.scrollY
            snapping back to exactly bodyHeight-viewportHeight at that same
            instant. Isolating each boundary means one section's chunk still
            loading can no longer touch any other section that's already
            rendered. */}
        <Suspense fallback={null}>
          <LazyMount anchorId="call-sim" minHeightClass="min-h-[1467px] lg:min-h-[1019px]"><CallSimulation /></LazyMount>
        </Suspense>
        <Problem />
        {/* minHeightClass values are measured against the real production
            build (getBoundingClientRect on the mounted section), not
            guessed, at BOTH the component's own responsive breakpoint AND
            at a real 390x844 mobile viewport (Puppeteer + touch emulation,
            scrolled through like an actual visit so every section's own
            IntersectionObserver fires). A mismatch here is what was
            causing a large layout shift (CLS ~0.9, measured) every time a
            section resolved from placeholder to real content — the shift
            showed up on the footer because everything below the resolving
            section moves with it. Components that switch from a
            multi-column grid to a stacked single column below their own
            breakpoint (lg=1024 or md=768, matching that component's own
            grid class) get the measured mobile-width value below that same
            breakpoint; the desktop value after the prefix is the value
            measured at that breakpoint. */}
        <Suspense fallback={null}>
          <LazyMount anchorId="how-it-works" minHeightClass="min-h-[1676px] md:min-h-[842px]"><HowItWorks /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount anchorId="voices" minHeightClass="min-h-[2076px] md:min-h-[1069px]"><VoiceLibrary /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount minHeightClass="min-h-[1416px] md:min-h-[873px]"><RealWorld /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount minHeightClass="min-h-[1039px] lg:min-h-[940px]"><CaseStudy /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount minHeightClass="min-h-[1641px] lg:min-h-[786px]"><Guardrails /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount anchorId="pricing" minHeightClass="min-h-[1990px] md:min-h-[1421px]"><Pricing /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount minHeightClass="min-h-[805px] lg:min-h-[642px]"><RiskReversal /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount anchorId="faq" minHeightClass="min-h-[874px] lg:min-h-[717px]"><FAQ /></LazyMount>
        </Suspense>
        <Suspense fallback={null}>
          <LazyMount minHeightClass="min-h-[608px] md:min-h-[1010px]"><FinalCTA /></LazyMount>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
